// Ask HR: history on the left, the conversation on the right. Answers stream in with numbered
// links to the page they came from. "/" is a new chat; "/c/<id>" is a saved one.
// The browser's own scroll bar scrolls the messages. The header, the left pane and the question
// box are pinned (sticky), and the page doesn't rubber-band, so they never move or bounce. Inside
// the pane the history list scrolls on its own. Up to 900px wide the pane is a drawer instead,
// opened from the bar above the conversation, so the conversation gets the whole width.

import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from "react";
import { api } from "../api.js";
import { AnswerNotices, AnswerText, SourceList } from "./Answer.js";
import { Backdrop } from "./Backdrop.js";
import { postChat, type LocationSummary, type Notice, type Source, type WorkLocation } from "./client.js";
import { hotlineUrl } from "../config.js";
import { navigate } from "../router.js";
import { ComposerNote } from "./ComposerNote.js";
import { LocationAsk } from "./Location.js";
import { MenuIcon, SearchIcon, TrashIcon, highlight, relative } from "./listing.js";
import { MyRequest, hasUnseenComment, requestIdOf } from "./MyRequest.js";
import { RequestsPane } from "./RequestsPane.js";
import { TalkToHr } from "./TalkToHr.js";
import type { EmployeeRequest } from "@ask-hr/core/escalations";
import { parsePlace } from "@ask-hr/core/sources";

interface Conversation {
  id: string;
  title: string;
  updatedAt: string;
}

interface StoredMessage {
  id: string;
  role: "user" | "assistant";
  text: string;
  sources?: Source[];
  feedback?: "up" | "down";
  notices?: Notice[];
}

interface UiMessage {
  key: string;
  id?: string;
  role: "user" | "assistant";
  text: string;
  sources: Source[];
  pending?: boolean;
  error?: string;
  feedback?: "up" | "down";
  notices?: Notice[];
}

const SUGGESTIONS = [
  "How many paid holidays do we get?",
  "What's the daily meal limit when I travel?",
  "How does PTO accrue?",
  "How does tuition reimbursement work?",
];

export function conversationIdOf(path: string): string | null {
  const m = /^\/c\/([0-9a-z]{9,40})$/.exec(path);
  return m?.[1] ?? null;
}


// The width where the left pane becomes a drawer (base.css, same breakpoint).
const DRAWER_QUERY = "(max-width: 900px)";

function useMatches(query: string): boolean {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const on = () => setMatches(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, [query]);
  return matches;
}

export function ChatPage({ path, firstName }: { path: string; firstName: string }) {
  const routeId = conversationIdOf(path);
  const requestId = requestIdOf(path);
  const [conversations, setConversations] = useState<Conversation[] | null>(null);
  const [messages, setMessages] = useState<UiMessage[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(routeId);
  const [location, setLocation] = useState<LocationSummary | null>(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const drawer = useMatches(DRAWER_QUERY);
  // Open for one page at one width: any navigation (a chat, a request, New) or the window getting
  // wide closes it.
  const [openFor, setOpenFor] = useState<string | null>(null);
  const historyOpen = openFor === `${path}|${drawer}`;
  const setHistoryOpen = (open: boolean) => setOpenFor(open ? `${path}|${drawer}` : null);
  const menuRef = useRef<HTMLButtonElement>(null);
  const paneRef = useRef<HTMLElement>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [confirmAll, setConfirmAll] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [loadError, setLoadError] = useState(false);
  const [talkFor, setTalkFor] = useState<string | null>(null);
  // Questions asked before Ask HR knows where they live: shown, and answered in order once they say.
  const [held, setHeld] = useState<string[]>([]);
  const [requests, setRequests] = useState<EmployeeRequest[]>([]);
  const streamingInto = useRef<string | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const nextKey = useRef(0);

  const loadRequests = () =>
    api<{ requests: EmployeeRequest[] }>("GET", "/api/escalations").then(
      (r) => setRequests(r.requests),
      () => {},
    );

  const loadConversations = () =>
    api<{ conversations: Conversation[] }>("GET", "/api/conversations").then(
      (r) => setConversations(r.conversations),
      () => setConversations([]),
    );

  useEffect(() => {
    let live = true;
    api<{ conversations: Conversation[] }>("GET", "/api/conversations").then(
      (r) => live && setConversations(r.conversations),
      () => live && setConversations([]),
    );
    api<{ requests: EmployeeRequest[] }>("GET", "/api/escalations").then(
      (r) => live && setRequests(r.requests),
      () => {},
    );
    api<{ location: LocationSummary }>("GET", "/api/me").then(
      (r) => live && setLocation(r.location),
      () => {},
    );
    return () => {
      live = false;
    };
  }, []);

  // Open a saved conversation when the path changes (not the one being streamed into).
  useEffect(() => {
    if (routeId && routeId === streamingInto.current) return;
    let live = true;
    if (!routeId) {
      queueMicrotask(() => {
        if (!live) return;
        setMessages([]);
        setConversationId(null);
        setLoadError(false);
      });
      return () => {
        live = false;
      };
    }
    api<{ messages: StoredMessage[] }>("GET", `/api/conversations/${routeId}`).then(
      (r) => {
        if (!live) return;
        setConversationId(routeId);
        setLoadError(false);
        setMessages(
          r.messages.map((m) => ({
            key: m.id,
            id: m.id,
            role: m.role,
            text: m.text,
            sources: m.sources ?? [],
            ...(m.feedback ? { feedback: m.feedback } : {}),
            ...(m.notices?.length ? { notices: m.notices } : {}),
          })),
        );
      },
      () => live && setLoadError(true),
    );
    return () => {
      live = false;
    };
  }, [routeId]);

  // The pane pins right under the header, whose height changes when it wraps on narrow screens.
  useEffect(() => {
    const head = document.querySelector<HTMLElement>(".sitehead");
    if (!head) return;
    const set = () => document.documentElement.style.setProperty("--head-h", `${head.offsetHeight}px`);
    set();
    const ro = new ResizeObserver(set);
    ro.observe(head);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (searchOpen) searchRef.current?.focus();
  }, [searchOpen]);

  // Open: focus moves into it and Escape closes it, back to the menu button.
  useEffect(() => {
    if (!drawer || !historyOpen) return;
    paneRef.current?.querySelector<HTMLElement>("button, a")?.focus();
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setOpenFor(null);
      menuRef.current?.focus();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [drawer, historyOpen]);

  // No overscroll bounce while the chat is open (base.css: html.chat-shell).
  useEffect(() => {
    document.documentElement.classList.add("chat-shell");
    return () => document.documentElement.classList.remove("chat-shell");
  }, []);

  // Follow a streaming answer only if the reader is already at the bottom. Scrolling up to read
  // earlier messages must never be yanked back down by the next chunk of text.
  const following = useRef(true);
  useEffect(() => {
    const onScroll = () => {
      const fromBottom = document.documentElement.scrollHeight - (window.scrollY + window.innerHeight);
      following.current = fromBottom < 160;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    // The bottom of the page puts the newest text just above the pinned question box.
    if (following.current) window.scrollTo({ top: document.documentElement.scrollHeight });
  }, [messages]);

  const patchLast = (fn: (m: UiMessage) => UiMessage) =>
    setMessages((ms) => ms.map((m, i) => (i === ms.length - 1 ? fn(m) : m)));

  // Until they've said where they live, questions wait on screen (nothing is sent) and a place
  // typed into the question box is taken as the answer.
  const needsPlace = location !== null && location.source !== "chosen";
  async function send(text: string) {
    const question = text.trim();
    if (!question || busy) return;
    if (needsPlace || held.length) {
      setDraft("");
      following.current = true;
      const place = parsePlace(question);
      if (place) void choosePlace(place);
      else setHeld((h) => [...h, question]);
      return;
    }
    await sendNow(question);
  }

  async function choosePlace(place: WorkLocation) {
    const waiting = held;
    setHeld([]);
    if (!(await chooseLocation(place))) {
      setHeld(waiting);
      return;
    }
    // In order, into one conversation: the first creates it if this is a new chat.
    let conv = conversationId;
    for (const q of waiting) conv = await sendNow(q, conv);
  }

  // Sends one question and streams the answer; returns the conversation it went into.
  async function sendNow(question: string, conv: string | null = conversationId): Promise<string | null> {
    if (busy) return conv;
    let into = conv;
    setBusy(true);
    setDraft("");
    following.current = true; // asking a question jumps to the new answer
    const stamp = String((nextKey.current += 1));
    setMessages((ms) => [
      ...ms,
      { key: `u${stamp}`, role: "user", text: question, sources: [] },
      { key: `a${stamp}`, role: "assistant", text: "", sources: [], pending: true },
    ]);
    try {
      await postChat({ ...(conv ? { conversationId: conv } : {}), message: question }, (line) => {
        if (line.t === "meta") {
          setLocation(line.location);
          into = line.conversationId;
          if (!conv) {
            streamingInto.current = line.conversationId;
            setConversationId(line.conversationId);
            navigate(`/c/${line.conversationId}`);
          }
        } else if (line.t === "text") {
          patchLast((m) => ({ ...m, text: m.text + line.v }));
        } else if (line.t === "source") {
          patchLast((m) => ({ ...m, sources: [...m.sources, line.source] }));
        } else if (line.t === "notices") {
          patchLast((m) => ({ ...m, notices: line.notices }));
        } else if (line.t === "needs_location") {
          // Nothing was answered or saved: the question waits (shown) while they say where they live.
          setMessages((ms) => ms.slice(0, -2));
          setHeld((h) => [...h, line.question]);
          setLocation(line.location);
        } else if (line.t === "done") {
          patchLast((m) => ({ ...m, id: line.messageId, pending: false }));
        } else if (line.t === "error") {
          patchLast((m) => ({ ...m, pending: false, error: line.message }));
        }
      });
    } catch {
      patchLast((m) => ({ ...m, pending: false, error: "Couldn't reach Ask HR. Check your connection and try again." }));
    } finally {
      patchLast((m) => (m.pending ? { ...m, pending: false } : m));
      setBusy(false);
      void loadConversations();
      inputRef.current?.focus();
    }
    return into;
  }

  async function rate(m: UiMessage, rating: "up" | "down") {
    if (!m.id || !conversationId) return;
    const next = m.feedback === rating ? null : rating;
    setMessages((ms) => ms.map((x) => (x.key === m.key ? { ...x, ...(next ? { feedback: next } : { feedback: undefined }) } : x)) as UiMessage[]);
    try {
      await api("PUT", `/api/conversations/${conversationId}/messages/${m.id}/feedback`, { rating: next });
    } catch {}
  }

  async function remove(id: string) {
    try {
      await api("DELETE", `/api/conversations/${id}`);
    } catch {}
    setConfirmDelete(null);
    if (id === conversationId) navigate("/");
    void loadConversations();
  }

  async function clearAll() {
    try {
      await api("DELETE", "/api/conversations");
    } catch {}
    setConfirmAll(false);
    if (conversationId) navigate("/");
    void loadConversations();
  }

  async function chooseLocation(place: WorkLocation): Promise<boolean> {
    try {
      setLocation((await api<{ location: LocationSummary }>("PUT", "/api/me/location", { place })).location);
      return true;
    } catch {
      return false;
    }
  }

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    void send(draft);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      void send(draft);
    }
  };

  const term = search.trim().toLowerCase();
  const shown = conversations?.filter((c) => !term || c.title.toLowerCase().includes(term)) ?? null;
  const toggleSearch = () => {
    if (searchOpen) setSearch("");
    setSearchOpen(!searchOpen);
  };

  const newChat = () => {
    streamingInto.current = null;
    setHistoryOpen(false);
    navigate("/");
    inputRef.current?.focus();
  };

  const updates = requests.filter(hasUnseenComment).length;
  const heading = requestId
    ? "Your HR request"
    : (conversations?.find((c) => c.id === conversationId)?.title ?? messages.find((m) => m.role === "user")?.text ?? held[0] ?? "New chat");

  return (
    <div className="chat">
      <Backdrop />
      {drawer && historyOpen ? <div className="scrim" aria-hidden="true" onClick={() => setHistoryOpen(false)} /> : null}
      <aside
        ref={paneRef}
        className={`history${historyOpen ? " open" : ""}`}
        aria-label="Your conversations"
        id="chat-pane"
        inert={drawer && !historyOpen}
      >
        <div className="pane-top">
          <div className="top-row">
            <button type="button" className="primary" onClick={newChat} disabled={busy}>
              New chat
            </button>
            <button type="button" className="iconbtn" aria-label="Search chats" title="Search chats" aria-pressed={searchOpen} onClick={toggleSearch}>
              <SearchIcon />
            </button>
            <button
              type="button"
              className="iconbtn"
              aria-label="Clear all chats"
              title="Clear all chats"
              aria-expanded={confirmAll}
              disabled={busy || !conversations?.length}
              onClick={() => setConfirmAll(!confirmAll)}
            >
              <TrashIcon />
            </button>
          </div>
          {searchOpen ? (
            <label className="chat-search">
              <SearchIcon />
              <span className="sr-only">Search chats</span>
              <input
                ref={searchRef}
                type="search"
                placeholder="Search chats"
                autoComplete="off"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") toggleSearch();
                }}
              />
            </label>
          ) : null}
          {confirmAll && conversations?.length ? (
            <div className="confirm-all" role="alert">
              <span>
                Delete all <b>{conversations.length}</b> conversations? This can't be undone.
              </span>
              <div className="actions start">
                <button type="button" className="danger" onClick={() => void clearAll()}>
                  Delete all
                </button>
                <button type="button" onClick={() => setConfirmAll(false)}>
                  Keep
                </button>
              </div>
            </div>
          ) : null}
        </div>
        <nav className="history-list" aria-label="Chat history">
          {shown === null ? (
            <p className="sub">Loading…</p>
          ) : conversations?.length === 0 ? (
            <p className="sub">Your questions will appear here. They're kept for 90 days.</p>
          ) : shown.length === 0 ? (
            <p className="sub">No chats match.</p>
          ) : (
            <ul>
              {shown.map((c) => (
                <li key={c.id} className={c.id === conversationId ? "current" : undefined}>
                  {confirmDelete === c.id ? (
                    <div className="confirm">
                      <span>Delete this conversation?</span>
                      <button type="button" className="danger" onClick={() => void remove(c.id)}>
                        Delete
                      </button>
                      <button type="button" onClick={() => setConfirmDelete(null)}>
                        Keep
                      </button>
                    </div>
                  ) : (
                    <>
                      <a
                        href={`/c/${c.id}`}
                        aria-current={c.id === conversationId ? "page" : undefined}
                        onClick={(e) => {
                          e.preventDefault();
                          if (busy) return;
                          streamingInto.current = null;
                          setHistoryOpen(false);
                          navigate(`/c/${c.id}`);
                        }}
                      >
                        <span className="title">{highlight(c.title, term)}</span>
                        <span className="when">{relative(c.updatedAt)}</span>
                      </a>
                      <button type="button" className="icon" aria-label={`Delete "${c.title}"`} onClick={() => setConfirmDelete(c.id)}>
                        ×
                      </button>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
        </nav>
        <div className="pane-bottom">
          <RequestsPane requests={requests} currentId={requestId} onChanged={() => void loadRequests()} />
          <button
            type="button"
            onClick={() => {
              setHistoryOpen(false);
              setTalkFor("general");
            }}
          >
            Talk to HR
          </button>
        </div>
      </aside>

      <div className="mobilebar">
        <button
          ref={menuRef}
          type="button"
          aria-label={updates ? `Chats and HR requests, ${updates} ${updates === 1 ? "update" : "updates"}` : "Chats and HR requests"}
          aria-expanded={historyOpen}
          aria-controls="chat-pane"
          onClick={() => setHistoryOpen(!historyOpen)}
        >
          <MenuIcon />
          {updates ? <span className="badge">{updates}</span> : null}
        </button>
        <span className="t">{heading}</span>
        <button type="button" className="primary" onClick={newChat} disabled={busy}>
          <span aria-hidden="true">+</span> New
        </button>
      </div>

      {requestId ? (
        <section className="conversation" aria-label="Your HR request">
          <MyRequest key={requestId} id={requestId} onSeen={() => setRequests((rs) => [...rs])} />
        </section>
      ) : (
        <section className="conversation" aria-label="Conversation">
          <div className="messages" aria-busy={busy}>
            {loadError ? (
              <p className="notice" role="alert">
                That conversation couldn't be opened. It may have been deleted, or it's older than 90 days.
              </p>
            ) : null}
            {messages.length === 0 && held.length === 0 && !loadError ? (
              <div className="welcome">
                <h2>Hi {firstName}, what can I help you find?</h2>
                <p className="sub">
                  Ask about Novara HR policy: time off, holidays, benefits, travel and expenses, and more. Every answer links to
                  the HR document it came from.
                </p>
                <div className="suggestions">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} type="button" onClick={() => void send(s)} disabled={busy}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((m) =>
                m.role === "user" ? (
                  <div key={m.key} className="msg mine">
                    <p>{m.text}</p>
                  </div>
                ) : (
                  <div key={m.key} className="msg bot">
                    <AnswerNotices notices={m.notices} pending={Boolean(m.pending)} hotlineUrl={hotlineUrl} />
                    {m.text ? <AnswerText text={m.text} sources={m.sources} /> : m.pending ? <p className="typing">Looking through the HR documents…</p> : null}
                    {m.error ? (
                      <p className="notice" role="alert">
                        {m.error}
                      </p>
                    ) : null}
                    {!m.pending ? <SourceList sources={m.sources} /> : null}
                    {m.id && !m.pending && !m.error ? (
                      <div className="rate" role="group" aria-label="Was this helpful?">
                        <button type="button" className="icon" aria-pressed={m.feedback === "up"} aria-label="Helpful" onClick={() => void rate(m, "up")}>
                          👍
                        </button>
                        <button type="button" className="icon" aria-pressed={m.feedback === "down"} aria-label="Not helpful" onClick={() => void rate(m, "down")}>
                          👎
                        </button>
                        <button type="button" className="talk-btn" onClick={() => setTalkFor(m.key)}>
                          Talk to HR
                        </button>
                      </div>
                    ) : null}
                    {talkFor === m.key ? (
                      <TalkToHr conversationId={conversationId} onClose={() => setTalkFor(null)} onSent={() => void loadRequests()} />
                    ) : null}
                  </div>
                ),
              )
            )}
            {held.map((q, i) => (
              <div key={`held${i}`} className="msg mine">
                <p>{q}</p>
              </div>
            ))}
            {held.length && location ? (
              <div className="msg bot">
                <LocationAsk onChoose={(place) => void choosePlace(place)} />
              </div>
            ) : null}
            {talkFor === "general" ? (
              <TalkToHr conversationId={conversationId} onClose={() => setTalkFor(null)} onSent={() => void loadRequests()} />
            ) : null}
          </div>

          {/* Asked once, before the first answer: here until they ask something, then in the thread. */}
          {location && needsPlace && !held.length ? (
            <LocationAsk onChoose={(place) => void choosePlace(place)} />
          ) : null}
          <form className="composer" onSubmit={onSubmit}>
            <label className="sr-only" htmlFor="question">
              Your question
            </label>
            <textarea
              id="question"
              ref={inputRef}
              rows={2}
              maxLength={4000}
              placeholder={
                needsPlace
                  ? drawer
                    ? "Type your state, or a question"
                    : "Type your state, or a question to answer after"
                  : drawer
                    ? "Ask about Novara HR policy"
                    : "Ask a question about Novara HR policy"
              }
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              disabled={busy}
            />
            <button type="submit" className="primary ask" disabled={busy || !draft.trim()} aria-label={busy ? "Answering" : "Ask"}>
              <span className="label">{busy ? "Answering…" : "Ask"}</span>
              <span className="arrow" aria-hidden="true">
                ↑
              </span>
            </button>
            <ComposerNote />
          </form>
        </section>
      )}
    </div>
  );
}
